import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import axios from 'axios';
import { FileText, Calendar, TrendingUp, Eye, ExternalLink, X } from 'lucide-react';
import ShareButton from '@/components/ShareButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORIES = [
  { id: 'all', label: 'Toutes' },
  { id: 'rapport', label: 'Rapports d\'activité' },
  { id: 'manifeste', label: 'Manifeste & Vision' },
  { id: 'etude', label: 'Études & Recherches' },
  { id: 'brochure', label: 'Brochures' },
  { id: 'presentation', label: 'Présentations' },
  { id: 'autre', label: 'Autres' },
];

const CATEGORY_COLORS = {
  rapport: 'bg-[#4A6B48]/10 text-[#4A6B48]',
  manifeste: 'bg-[#D87D4A]/10 text-[#D87D4A]',
  etude: 'bg-blue-100 text-blue-700',
  brochure: 'bg-purple-100 text-purple-700',
  presentation: 'bg-pink-100 text-pink-700',
  autre: 'bg-gray-100 text-gray-700',
};

const formatSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
};

const resolveUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  return `${BACKEND_URL}${url}`;
};export default function DocumentsPage() {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const initialCategory = (() => {
    if (typeof window === 'undefined') return 'all';
    const c = new URLSearchParams(window.location.search).get('category');
    return c || 'all';
  })();
  const [active, setActive] = useState(initialCategory);
  const [previewDoc, setPreviewDoc] = useState(null);

  const fetch = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/documents`);
      setDocs(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  // Auto-open preview if URL contains ?doc=<id> (used by Share links)
  useEffect(() => {
    if (loading || docs.length === 0) return;
    const params = new URLSearchParams(window.location.search);
    const docId = params.get('doc');
    if (docId) {
      const found = docs.find((d) => d.document_id === docId);
      if (found) setPreviewDoc(found);
    }
  }, [loading, docs]);

  const filtered = useMemo(() => {
    if (active === 'all') return docs;
    return docs.filter((d) => d.category === active);
  }, [docs, active]);

  return (
    <div className="min-h-screen flex flex-col">
      <Navigation />

      <section className="section-padding bg-white" data-testid="documents-page">
        <div className="container mx-auto px-6">
          <div className="text-center mb-10">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-[#4A6B48]/10 mb-4">
              <FileText className="text-[#4A6B48]" size={40} />
            </div>
            <h1
              className="text-4xl sm:text-5xl font-bold text-[#2E332D] mb-4"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
              data-testid="documents-title"
            >
              Rapports & Documents
            </h1>
            <p className="text-base text-[#5C665A] max-w-3xl mx-auto leading-relaxed mb-3 font-semibold italic">
              Découvrez comment vous pouvez contribuer à la mise en route des îlots de développement
              auto-gérés en RD Congo.
            </p>
            <p className="text-base text-[#5C665A] max-w-2xl mx-auto leading-relaxed">
              Consultez en ligne nos rapports d&apos;activité, études, brochures et autres
              documents publics retraçant notre démarche d&apos;îlots de développement intégral.
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-2 mb-8">
            {CATEGORIES.map((c) => (
              <button
                key={c.id}
                onClick={() => setActive(c.id)}
                data-testid={`docs-cat-${c.id}`}
                className={`px-4 py-2 rounded-full text-sm font-bold border-2 transition-all ${
                  active === c.id
                    ? 'bg-[#4A6B48] border-[#4A6B48] text-white'
                    : 'border-[#E5E3DB] text-[#5C665A] hover:border-[#4A6B48] hover:text-[#4A6B48]'
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>

          {loading && (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#4A6B48]"></div>
            </div>
          )}

          {!loading && filtered.length === 0 && (
            <div className="text-center py-12" data-testid="no-docs-message">
              <p className="text-[#5C665A]">Aucun document disponible dans cette catégorie.</p>
            </div>
          )}

          {!loading && filtered.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 max-w-6xl mx-auto">
              {filtered.map((doc) => (
                <article
                  key={doc.document_id}
                  className="card hover:-translate-y-1 transition-transform"
                  data-testid={`document-card-${doc.document_id}`}
                >
                  <div className="flex items-center gap-2 mb-3 flex-wrap">
                    <span className={`text-xs font-bold px-2 py-1 rounded ${CATEGORY_COLORS[doc.category] || 'bg-gray-100'}`}>
                      {CATEGORIES.find((c) => c.id === doc.category)?.label || doc.category}
                    </span>
                    {doc.year && (
                      <span className="text-xs text-[#5C665A] inline-flex items-center gap-1">
                        <Calendar size={12} /> {doc.year}
                      </span>
                    )}
                  </div>
                  <h2
                    className="text-xl font-bold text-[#2E332D] mb-2"
                    style={{ fontFamily: 'Cormorant Garamond, serif' }}
                  >
                    {doc.title}
                  </h2>
                  {doc.description && (
                    <p className="text-sm text-[#5C665A] mb-4 line-clamp-3">{doc.description}</p>
                  )}
                  <div className="flex items-center justify-between border-t border-[#E5E3DB] pt-3 mt-3">
                    <div className="flex flex-col gap-1">
                      <span className="text-xs text-[#5C665A] line-clamp-1">
                        {doc.original_filename}
                        {doc.file_size > 0 && ` · ${formatSize(doc.file_size)}`}
                      </span>
                      {doc.download_count > 0 && (
                        <span className="text-xs text-[#4A6B48] inline-flex items-center gap-1 font-semibold">
                          <TrendingUp size={11} />
                          {doc.download_count} consultation{doc.download_count > 1 ? 's' : ''}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <ShareButton
                        title={doc.title}
                        url={`${window.location.origin}/documents/view/${doc.document_id}`}
                        message="Découvrez ce rapport de CRESA ASBL :"
                        testId={`share-doc-${doc.document_id}`}
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setPreviewDoc(doc);
                          axios.post(`${API}/documents/${doc.document_id}/download`).catch(() => {});
                        }}
                        data-testid={`consult-doc-${doc.document_id}`}
                        className="inline-flex items-center gap-1 bg-[#4A6B48] text-white text-sm font-bold px-4 py-2 rounded-md hover:bg-[#3B5539]"
                      >
                        <Eye size={14} />
                        Consulter
                      </button>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />

      {/* Inline view-only preview modal (no download allowed) */}
      {previewDoc && (() => {
        const fileUrl = resolveUrl(previewDoc.file_url);
        const viewerSrc = `${fileUrl}#toolbar=0&navpanes=0&scrollbar=1&statusbar=0&messages=0`;
        const fullScreenUrl = `/documents/view/${previewDoc.document_id}`;
        return (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-4" style={{ zIndex: 9999 }} data-testid="pdf-preview-modal" onContextMenu={(e) => e.preventDefault()}>
            <div className="bg-white rounded-lg w-full max-w-5xl h-[90vh] flex flex-col shadow-2xl">
              <div className="flex items-center justify-between px-4 py-3 border-b border-[#E5E3DB]">
                <h3 className="font-bold text-[#2E332D] truncate">{previewDoc.title}</h3>
                <div className="flex items-center gap-3 shrink-0">
                  <a
                    href={fullScreenUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="open-in-new-tab"
                    className="text-xs text-[#4A6B48] hover:underline inline-flex items-center gap-1 font-semibold"
                  >
                    <ExternalLink size={12} /> Ouvrir dans un nouvel onglet
                  </a>
                  <span className="text-xs text-[#5C665A] italic hidden sm:inline">Consultation uniquement</span>
                  <button onClick={() => setPreviewDoc(null)} data-testid="close-pdf-preview" className="p-2 hover:bg-gray-100 rounded">
                    <X size={20} />
                  </button>
                </div>
              </div>
              <div className="flex-1 bg-[#F9F8F6] relative" onContextMenu={(e) => e.preventDefault()}>
                <iframe
                  title={previewDoc.title}
                  src={viewerSrc}
                  className="w-full h-full"
                  sandbox="allow-scripts allow-same-origin"
                />
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
